import { intervalToDuration } from "date-fns";


export const calculateExpectedFee = (formData: any, recruitment: any): number => {
  const pInfo = (formData?.personalInfo || {}) as Record<string, unknown>;
  if (!pInfo.categoryOfCandidate) return 0;
  const recPayment = recruitment?.paymentDetails as { amount?: Record<string, number> } | undefined;
  const amounts = recPayment?.amount || {};
  const candidatesFees: number[] = [];

  const cat = pInfo.categoryOfCandidate as string;
  if (amounts[cat] !== undefined && amounts[cat] !== null && (amounts[cat] as any) !== "" && !isNaN(Number(amounts[cat]))) {
    candidatesFees.push(Number(amounts[cat]));
  }

  if (
    amounts.PWD !== undefined &&
    amounts.PWD !== null &&
    (amounts.PWD as any) !== "" &&
    !isNaN(Number(amounts.PWD)) &&
    Boolean(pInfo.isPWD)
  ) {
    candidatesFees.push(Number(amounts.PWD));
  }

  const exFee = amounts.EX_SERVICEMAN !== undefined ? amounts.EX_SERVICEMAN : amounts.EX_SERVICEMEN;
  const isExServiceman = Boolean(
    pInfo.exServiceMan === true ||
    pInfo.exServiceMan === "true" ||
    pInfo.exServiceMan === "YES" ||
    pInfo.exServiceMan === "Yes"
  );
  if (
    exFee !== undefined &&
    exFee !== null &&
    (exFee as any) !== "" &&
    !isNaN(Number(exFee)) &&
    isExServiceman
  ) {
    candidatesFees.push(Number(exFee));
  }

  const gender = String(pInfo.gender || "").toUpperCase();
  const isFemale = gender === "FEMALE" || gender.startsWith("F");
  if (
    amounts.FEMALE !== undefined &&
    amounts.FEMALE !== null &&
    (amounts.FEMALE as any) !== "" &&
    !isNaN(Number(amounts.FEMALE)) &&
    isFemale
  ) {
    candidatesFees.push(Number(amounts.FEMALE));
  }

  if (candidatesFees.length === 0) return 0;
  console.log("Fees Found..", Math.min(...candidatesFees));
  return Math.min(...candidatesFees);
};


export function validateForm(schema: any, formData: any, recruitment: any = {}): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!schema || !Array.isArray(schema.sections)) return errors;

  schema.sections.forEach((section: any) => {
    const sectionId = section.id;
    const sectionData = formData[sectionId] || {};

    section.fields.forEach((field: any) => {
      const fieldId = field.id;
      const value = sectionData[fieldId];
      const path = `${sectionId}.${fieldId}`;

      validateField(field, value, path, errors, formData, recruitment);
    });
  });

  // Business logic validations
  const personalInfo = formData.personalInfo || {};
  const hasPersonalInfo = schema.sections.some((sec: any) => sec.id === "personalInfo");
  const hasExperience = schema.sections.some((sec: any) => sec.id === "experience");
  const hasCouncilReg = schema.sections.some((sec: any) => sec.id === "councilRegistration");

  // 1 & 2. Personal Info validations (Candidate Type & Seat Matrix)
  if (hasPersonalInfo) {
    // 1. Candidate Type Validation
    if (personalInfo.department && personalInfo.candidateType && recruitment.departments) {
      const selectedDept = recruitment.departments.find((d: any) => d.name === personalInfo.department);
      if (selectedDept) {
        const candidateType = String(personalInfo.candidateType).toUpperCase().replace("_", "-");
        const deptCandidateType = String(selectedDept.candidateType || "").toUpperCase().replace("_", "-");
        if (deptCandidateType === "MEDICAL" && candidateType === "NON-MEDICAL") {
          errors["personalInfo.candidateType"] = "Only Medical candidates are eligible for this post.";
        }
      }
    }

    // 2. Post Category (Seat Matrix) Validation
    if (personalInfo.department && personalInfo.postName && personalInfo.categoryOfCandidate && recruitment.departments) {
      const selectedDept = recruitment.departments.find((d: any) => d.name === personalInfo.department);
      const vacancy = selectedDept?.vacancies?.find((v: any) => v.post === personalInfo.postName);
      if (vacancy) {
        const candidateCat = personalInfo.categoryOfCandidate;
        const cats = vacancy.categories || { UR: 0, OBC: 0, SC: 0, ST: 0, EWS: 0 };
        let eligible = false;
        if (candidateCat === "UR") {
          eligible = (cats.UR > 0);
        } else if (candidateCat === "OBC") {
          eligible = (cats.OBC > 0 || cats.UR > 0);
        } else if (candidateCat === "SC") {
          eligible = (cats.SC > 0 || cats.UR > 0);
        } else if (candidateCat === "ST") {
          eligible = (cats.ST > 0 || cats.UR > 0);
        } else if (candidateCat === "EWS") {
          eligible = (cats.EWS > 0 || cats.UR > 0);
        }
        if (!eligible) {
          errors["personalInfo.postName"] = "No eligible vacancy available for your category.";
        }
      }
    }
  }

  // 3 & 4. Experience Validation and Multiple Experience Records Merging
  if (hasExperience) {
    const experiences = formData.experience?.experienceList || [];
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    // Validate entries (dates, formats, ranges)
    for (let i = 0; i < experiences.length; i++) {
      const exp = experiences[i];
      if (!exp || typeof exp !== "object") continue;

      const isEmptyRow = !exp.organizationName && !exp.designation && !exp.serviceFrom && !exp.serviceTo;
      if (isEmptyRow) continue;

      if (!exp.serviceFrom) continue;
      const from = new Date(exp.serviceFrom);
      if (isNaN(from.getTime())) {
        errors[`experience.experienceList.${i}.serviceFrom`] = "Invalid start date";
        continue;
      }
      if (from > today) {
        errors[`experience.experienceList.${i}.serviceFrom`] = "Start date cannot be in the future";
        continue;
      }

      const isCurrent = exp.isCurrent === true || exp.isCurrent === "true" || exp.isCurrent === "YES" || exp.isCurrent === "Yes";
      const to = isCurrent ? new Date() : (exp.serviceTo ? new Date(exp.serviceTo) : null);
      if (!to) {
        errors[`experience.experienceList.${i}.serviceTo`] = "End date is required";
        continue;
      }
      if (isNaN(to.getTime())) {
        errors[`experience.experienceList.${i}.serviceTo`] = "Invalid end date";
        continue;
      }
      if (to > today) {
        errors[`experience.experienceList.${i}.serviceTo`] = "End date cannot be in the future";
        continue;
      }
      if (to < from) {
        errors[`experience.experienceList.${i}.serviceTo`] = "End date cannot be before start date";
        continue;
      }
    }

    const { years: totalYears, months: totalMonths } = calculateExperience(experiences);

    // Experience requirement check
    if (personalInfo.department && personalInfo.postName && recruitment.departments) {
      const selectedDept = recruitment.departments.find((d: any) => d.name === personalInfo.department);
      const vacancy = selectedDept?.vacancies?.find((v: any) => v.post === personalInfo.postName);
      if (vacancy) {
        const minExpYears = vacancy.requirements?.minExperienceYears || 0;
        const totalExperienceMonths = totalYears * 12 + totalMonths;
        const requiredMonths = minExpYears * 12;
        if (totalExperienceMonths < requiredMonths) {
          errors["experience.experienceList"] = `Minimum of ${minExpYears} years of experience is required for this post. You only have ${totalYears} years and ${totalMonths} months.`;
        }
      }
    }
  }
  // Council Registration validations
  if (hasCouncilReg && recruitment.generalRequirements?.councilRegistration?.required) {
    const councilReg = formData.councilRegistration || {};
    if (!councilReg.registeredWith || !String(councilReg.registeredWith).trim()) {
      errors["councilRegistration.registeredWith"] = "Registered Council Name is required.";
    }
    if (!councilReg.registrationNumber || !String(councilReg.registrationNumber).trim()) {
      errors["councilRegistration.registrationNumber"] = "Registration Number is required.";
    }
    if (!councilReg.registrationDate) {
      errors["councilRegistration.registrationDate"] = "Registration Date is required.";
    }
  }

  return errors;
}

function validateField(field: any, value: any, path: string, errors: Record<string, string>, formData: any = {}, recruitment: any = {}): void {
  const isMissing = value === undefined || value === null || value === "" ||
    (typeof value === "string" && value.trim() === "");

  if (isMissing) {
    if (field.id === "paymentCert") {
      const fee = calculateExpectedFee(formData, recruitment);
      if (fee === 0) return;
    }
    if (field.required) {
      errors[path] = `${field.label} is required`;
    }
    return;
  }

  switch (field.type) {
    case "email":
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (typeof value !== "string" || !emailRegex.test(value)) {
        errors[path] = "Please enter a valid email address";
      }
      break;

    case "phone":
      const phoneRegex = /^[6-9]\d{9}$/;
      if (typeof value !== "string" || !phoneRegex.test(value)) {
        errors[path] = "Please enter a valid 10-digit mobile number starting with 6-9";
      }
      break;

    case "number":
      const numValue = Number(value);
      if (isNaN(numValue)) {
        errors[path] = "Value must be a number";
      } else {
        if (field.validations?.min !== undefined && numValue < field.validations.min) {
          errors[path] = `Value must be at least ${field.validations.min}`;
        }
        if (field.validations?.max !== undefined && numValue > field.validations.max) {
          errors[path] = `Value must be at most ${field.validations.max}`;
        }
      }
      break;

    case "date":
      const dateVal = new Date(value);
      if (isNaN(dateVal.getTime())) {
        errors[path] = "Please enter a valid date";
      }
      break;

    case "address":
      if (typeof value !== "object") {
        errors[path] = "Invalid address structure";
      } else {
        const subFields = ["addressLine", "city", "state", "pincode"];
        subFields.forEach((sf) => {
          if (!value[sf] || String(value[sf]).trim() === "") {
            errors[`${path}.${sf}`] = `${sf} is required`;
          }
        });
        if (value.pincode && !/^\d{6}$/.test(value.pincode)) {
          errors[`${path}.pincode`] = "Pincode must be exactly 6 digits";
        }
      }
      break;

    case "file":
    case "image":
      if (typeof value !== "object" || !value.key) {
        errors[path] = "Please upload a document file";
      }
      break;

    case "repeater":
      if (Array.isArray(value)) {
        value.forEach((item: any, index: number) => {
          if (typeof item !== "object") {
            errors[`${path}.${index}`] = "Entry must be an object";
          } else {
            field.fields?.forEach((subField: any) => {
              validateField(subField, item[subField.id], `${path}.${index}.${subField.id}`, errors);
            });
          }
        });
      }
      break;

    case "select":
      if (field.options && !field.options.includes(value)) {
        errors[path] = `Value must be one of: ${field.options.join(", ")}`;
      }
      break;

    default:
      if (typeof value === "string") {
        if (field.validations?.minLength !== undefined && value.length < field.validations.minLength) {
          errors[path] = `Minimum length is ${field.validations.minLength} characters`;
        }
        if (field.validations?.maxLength !== undefined && value.length > field.validations.maxLength) {
          errors[path] = `Maximum length is ${field.validations.maxLength} characters`;
        }
        if (field.validations?.regex) {
          try {
            const rx = new RegExp(field.validations.regex);
            if (!rx.test(value)) {
              errors[path] = "Format is invalid";
            }
          } catch (e) {
            // ignore
          }
        }
      }
      break;
  }
}

export function calculateExperience(experiences: any[]): { years: number; months: number; days: number } {
  let totalYears = 0;
  let totalMonths = 0;
  let totalDays = 0;

  if (!Array.isArray(experiences) || experiences.length === 0) {
    return { years: 0, months: 0, days: 0 };
  }

  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const parsedIntervals = [];

  for (let i = 0; i < experiences.length; i++) {
    const exp = experiences[i];
    if (!exp || typeof exp !== "object") continue;
    const isEmptyRow = !exp.organizationName && !exp.designation && !exp.serviceFrom && !exp.serviceTo;
    if (isEmptyRow || !exp.serviceFrom) continue;
    const from = new Date(exp.serviceFrom);
    if (isNaN(from.getTime()) || from > today) continue;

    const isCurrent = exp.isCurrent === true || exp.isCurrent === "true" || exp.isCurrent === "YES" || exp.isCurrent === "Yes";
    const to = isCurrent ? new Date() : (exp.serviceTo ? new Date(exp.serviceTo) : null);
    if (!to || isNaN(to.getTime()) || to > today || to < from) continue;

    parsedIntervals.push({ from, to });
  }

  if (parsedIntervals.length > 0) {
    // Sort intervals by start date
    parsedIntervals.sort((a, b) => a.from.getTime() - b.from.getTime());

    // Merge overlapping and contiguous intervals
    const mergedIntervals: any[] = [];
    let currentInterval = {
      from: new Date(parsedIntervals[0].from),
      to: new Date(parsedIntervals[0].to)
    };

    const startOfDay = (d: any) => {
      const date = new Date(d);
      date.setHours(0, 0, 0, 0);
      return date;
    };

    const oneDayMs = 24 * 60 * 60 * 1000;

    for (let i = 1; i < parsedIntervals.length; i++) {
      const nextInterval = parsedIntervals[i];
      const currentToStart = startOfDay(currentInterval.to);
      const nextFromStart = startOfDay(nextInterval.from);

      if (nextFromStart.getTime() - currentToStart.getTime() <= oneDayMs) {
        if (nextInterval.to > currentInterval.to) {
          currentInterval.to = new Date(nextInterval.to);
        }
      } else {
        mergedIntervals.push(currentInterval);
        currentInterval = {
          from: new Date(nextInterval.from),
          to: new Date(nextInterval.to)
        };
      }
    }
    mergedIntervals.push(currentInterval);

    // Calculate sum of durations
    mergedIntervals.forEach((interval) => {
      const duration = intervalToDuration({ start: interval.from, end: interval.to });
      totalYears += duration.years || 0;
      totalMonths += duration.months || 0;
      totalDays += duration.days || 0;
    });

    // Normalize days to months (30 days = 1 month)
    totalMonths += Math.floor(totalDays / 30);
    totalDays = totalDays % 30;

    // Normalize months to years (12 months = 1 year)
    totalYears += Math.floor(totalMonths / 12);
    totalMonths = totalMonths % 12;
  }

  return { years: totalYears, months: totalMonths, days: totalDays };
}
